// src/utils/streamTitle.js
// Server-identical helpers for stream titles and per-user codes.
// 
// PUBLISHER COMPLIANCE (Capcom Community License, etc.):
// - Event names must NOT include game names
// - ✅ "Dojo Duel $18 to 1st" (event name - shown in UI)
// - ✅ "STREET FIGHTER 6" (game label - shown separately)
// - ❌ "Street Fighter 6 Dojo Duel $18 to 1st" (violates licensing)
// 
// Stream titles CAN include game abbreviation for Twitch/YouTube categorization:
// - ✅ "CritCash SF6 Dojo Duel $18 to 1st"

const GAME_ABBR = {
  // Shooters
  'Counter-Strike 2': 'CS2','Counter-Strike': 'CS','CS:GO': 'CSGO',
  "Tom Clancy's Rainbow Six Siege": 'R6','Rainbow Six Siege': 'R6',"Tom Clancy's Rainbow Six": 'R6',
  Valorant: 'VAL','Apex Legends': 'APEX','Overwatch 2': 'OW2',
  'Call of Duty': 'COD','Call of Duty: Warzone': 'WZ','Marvel Rivals': 'MR',
  // Fighters
  'Street Fighter 6': 'SF6','Tekken 8': 'TK8','Mortal Kombat 1': 'MK1','Mortal Kombat': 'MK',
  // Sports / Racing
  'EA Sports FC': 'EFC','EA Sports FC 26': 'EFC26','FIFA': 'FIFA',
  Madden: 'MDN','Madden NFL 26': 'MDN26','NBA 2K': 'N2K','NBA 2K Series': 'N2K','MLB The Show': 'MTS',
  'Forza Motorsport': 'FZM','Forza Horizon': 'FZH','Gran Turismo': 'GT7','Gran Turismo 7': 'GT7',
  'F1': 'F1','Rocket League': 'RKL',
  // MOBAs / RTS
  'League of Legends': 'LoL','Dota 2': 'DT2','StarCraft 2': 'SC2','Star Craft 2': 'SC2',
  // Smash
  'Super Smash Bros. Ultimate': 'SSB',
  // Mario Kart
  'Mario Kart World': 'MKW',
};

const SEP = ' • ';
const MAX_LEN = 100;

export const gameAbbr = (displayName, fallbackAbbr) =>
  GAME_ABBR[displayName] || fallbackAbbr || displayName || '';

const cleanJoin = (...p) => p.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();

export const makeEntryCode = (eventId, uid) =>
  (eventId && uid) ? `${String(eventId).slice(-6).toUpperCase()}-${String(uid).slice(0,6).toUpperCase()}` : '';

/**
 * Format first place prize with conservative rounding.
 *
 * Cash:  "$1.6K to 1st" / "$1.2M to 1st" / "$500 to 1st"  (amount is in CENTS)
 * Crits: "1.6K Crits to 1st" / "500 Crits to 1st"          (amount is WHOLE CRITS)
 *
 * @param {number} amount - cents (cash) OR whole Crits (when isCrits)
 * @param {boolean} isCrits - format as non-cash Crits (1 Crit = $1)
 */
export function formatFirstPrizeTo1st(amount, isCrits = false) {
  // Crits are whole units (no cents); cash is stored in cents.
  const whole = isCrits
    ? Math.floor(Number(amount || 0))
    : Math.floor(Number(amount || 0) / 100);
  const suffix = isCrits ? ' Crits to 1st' : ' to 1st';
  const sym = isCrits ? '' : '$';

  // Millions: floor to 0.1M increments
  if (whole >= 1_000_000) {
    const millions = Math.floor(whole / 100_000) / 10;
    return `${sym}${millions.toFixed(1).replace(/\.0$/, '')}M${suffix}`;
  }

  // Thousands: floor to 0.1K increments
  if (whole >= 1_000) {
    const thousands = Math.floor(whole / 100) / 10;
    return `${sym}${thousands.toFixed(1).replace(/\.0$/, '')}K${suffix}`;
  }

  // Under 1,000: exact amount
  if (whole > 0) {
    return `${sym}${whole}${suffix}`;
  }

  return null;
}

/**
 * Build EVENT DISPLAY NAME for lobby and event detail screens
 * 
 * PUBLISHER COMPLIANCE: Event name must NOT include game name.
 * Game name is displayed separately in UI via GAME_PROFILES[gameKey].displayName
 * 
 * Format: "{Bracket Type Name} ${Prize} to 1st"
 * Example: "Dojo Duel $100 to 1st"
 * 
 * @param {Object} params
 * @param {string} params.bracketTypeName - From gameMeta.js (e.g., "Dojo Duel")
 * @param {number} params.firstPlacePrizeCents - Prize in cents
 * @returns {string} Event display name (NO game name)
 */
export function makeEventDisplayName({
  gameDisplayName,  // Intentionally ignored for publisher compliance
  bracketTypeName,
  firstPlacePrizeCents,
  isCrits = false,
}) {
  const prize = formatFirstPrizeTo1st(firstPlacePrizeCents, isCrits);
  
  // PUBLISHER COMPLIANCE: NO game name in event name
  if (bracketTypeName && prize) {
    return `${bracketTypeName} ${prize}`;
  }
  if (bracketTypeName) {
    return bracketTypeName;
  }
  if (prize) {
    return prize;
  }
  return 'Event';
}

/**
 * Build a *base* (no per-user id) stream title that we store on the event.
 * Stream titles CAN include game abbreviation for Twitch/YouTube categorization.
 * 
 * Format: "CritCash {GameAbbr} {Bracket Type} ${Prize} to 1st"
 * Example: "CritCash SF6 Dojo Duel $100 to 1st"
 */
export function makeBaseStreamTitle({
  brand = 'CritCash',
  gameDisplayName,
  gameAbbreviation,
  bracketTypeName,       // e.g. "Dojo Duel", "Metro City Open"
  structureName,         // alias for bracketTypeName (backwards compat)
  firstPlacePrizeCents,
  formatLabel,           // "Best of 3", etc. (fallback)
  isCrits = false,
}) {
  const g = gameAbbr(gameDisplayName, gameAbbreviation);
  const prize = formatFirstPrizeTo1st(firstPlacePrizeCents, isCrits);
  const structure = bracketTypeName || structureName;
  
  // Stream title format: "CritCash SF6 Dojo Duel $100 to 1st"
  // Game abbreviation included for streaming platform categorization
  const candidates = [
    cleanJoin(brand, g, structure, prize),
    cleanJoin(brand, g, structure),
    cleanJoin(brand, g, formatLabel),
    cleanJoin(brand, g),
    brand,
  ];
  
  // Longest that fits MAX_LEN (we'll still trim further for per-user id)
  for (const t of candidates) if (t.length <= MAX_LEN) return t;
  return candidates[candidates.length - 1].slice(0, MAX_LEN);
}

/** Append a per-user code and hard-cap to 100 chars. */
export function composePerUserTitle(base, code, max = MAX_LEN) {
  const suffix = code ? (SEP + code) : '';
  const room = max - suffix.length;
  const safeBase = String(base || '').slice(0, Math.max(0, room)).trim();
  return safeBase + suffix;
}